import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiPackage } from 'react-icons/fi';
import productsService from '../../../../services/products.service';
import { useAuth } from '../../../../context/AuthContext';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';
import Pagination from '../../../../components/common/Pagination';
const ProductList = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState('');
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'productos') {
            setRunTour(true);
        }
        // fetchProducts se llamará a través del efecto de búsqueda/página
    }, [location.search]);

    // Efecto para buscar y paginar
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchProducts(currentPage, searchTerm);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [currentPage, searchTerm]);

    const fetchProducts = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const data = await productsService.getAll({ page, search });
            
            if (data?.results) {
                setProducts(data.results);
                setTotalCount(data.count || 0);
            } else if (Array.isArray(data)) {
                // Fallback para cuando el backend no devuelve paginación (aunque debería)
                setProducts(data);
                setTotalCount(data.length);
            } else {
                setProducts([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Error al cargar productos');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
            try {
                await productsService.delete(id);
                setProducts(products.filter(p => p.id !== id));
            } catch (err) {
                console.error('Error deleting product:', err);
                alert('No se pudo eliminar el producto. Puede que esté en uso en alguna cotización.');
            }
        }
    };


    // Ya no necesitamos filteredProducts local porque el servidor hace el trabajo
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Resetear a la primera página al buscar
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
    };

    const productosTourSteps = [
        {
            element: '#tour-productos-header',
            intro: '<strong>Productos y Servicios</strong>Aquí guardarás todo lo que vendes. Mantenerlo al día te ayudará a crear cotizaciones mucho más rápido.',
            position: 'bottom',
        },
        {
            element: '.header-search',
            intro: '<strong>Buscador</strong>Encuentra al instante cualquier producto de tu lista por nombre, marca o código SKU.',
            position: 'bottom',
        },
        {
            element: '#tour-productos-table',
            intro: '<strong>Tu Inventario</strong>Aquí verás todos los detalles: precio, descuento máximo permitido y disponibilidad de cada artículo.',
            position: 'top',
        },
        {
            element: '#tour-productos-nuevo',
            intro: '<strong>Agregar Producto</strong>Desde este botón podrás añadir más artículos para vender. Por ahora solo obsérvalo. ¡Sigamos con los Clientes!',
            position: 'left',
        }
    ];

    if (loading && products.length === 0) return <div className="loading-overlay"><div className="spinner"></div></div>;

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={productosTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Clientes' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/clientes?tour=clientes';
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-productos-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Gestión de Productos</h2>
                        <SectionHelpModal
                            title="¿Qué debes saber sobre tu Catálogo?"
                            content={
                                <div>
                                    <p>Esta es tu vitrina principal. Aquí registras todos los artículos o servicios que vas a vender.</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Tipos:</strong> Cuando crees un elemento, puedes definir si es un <em>Producto</em> (físico, inventariable) o un <em>Servicio</em> (una labor intelectual o manual).</li>
                                        <li><strong>Límites Estrictos:</strong> Los precios netos que fijes aquí son la base para el sistema. Los asesores no podrán vender por debajo del "Límite Máximo de Descuento" que tú establezcas al crear el producto.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Administra tu catálogo de productos y servicios</p>
                </div>
                {user?.rol === 'ADMIN' && (
                    <button id="tour-productos-nuevo" className="btn-custom btn-primary d-none d-lg-flex" onClick={() => navigate('/app/cotizaplus/productos/nuevo')}>
                        <FiPlus size={20} /> Nuevo Producto
                    </button>
                )}
            </div>

            {error && <div className="alert-custom alert-danger">{error}</div>}

            <div className="card-custom">
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                    <div className="header-search" style={{ flex: 1 }}>
                        <FiSearch size={20} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, SKU o marca..."
                            className="header-search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {products.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <FiPackage size={48} color="var(--gray-400)" />
                        <p className="text-muted" style={{ marginTop: '10px' }}>No se encontraron productos</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block" id="tour-productos-table">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Marca / Sku</th>
                                        <th>Tipo</th>
                                        <th>Precio Neto</th>
                                        <th>Impuesto</th>
                                        <th>Precio Final</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product.id}>
                                            <td>
                                                <div style={{ fontWeight: '500' }}>{product.nombre}</div>
                                                {product.descripcion && <small className="text-muted">{product.descripcion.substring(0, 30)}...</small>}
                                            </td>
                                            <td>
                                                <div>{product.marca || '-'}</div>
                                                <small className="text-muted">{product.sku || '-'}</small>
                                            </td>
                                            <td><span className="badge-custom badge-secondary">{product.tipo}</span></td>
                                            <td>{formatCurrency(product.precio)}</td>
                                            <td>{product.impuesto_total}%</td>
                                            <td style={{ fontWeight: 'bold' }}>
                                                {formatCurrency(productsService.calculateTotal(product.precio, product.impuesto_total))}
                                            </td>
                                            <td>
                                                <span className={`badge-custom ${product.activo ? 'badge-success' : 'badge-danger'}`}>
                                                    {product.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    {user?.rol === 'ADMIN' ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="btn-custom btn-sm"
                                                                onClick={() => navigate(`/app/cotizaplus/productos/editar/${product.id}`)}
                                                                title="Editar"
                                                                style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                                            >
                                                                <FiEdit2 size={16} /> Editar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-custom btn-sm"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleDelete(product.id);
                                                                }}
                                                                title="Eliminar"
                                                                style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                                            >
                                                                <FiTrash2 size={16} /> Eliminar
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/app/cotizaplus/productos/editar/${product.id}?mode=request`)}
                                                            title="Solicitar Cambio"
                                                            className="btn-custom"
                                                            style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}
                                                        >
                                                            Solicitar Cambio
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Vista Móvil: Tarjetas Apiladas */}
                        <div className="d-block d-lg-none mt-3">
                            {products.map((product) => (
                                <div key={product.id} className="mobile-card">
                                    <div className="mobile-card-header">
                                        <span className="mobile-card-id" style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                                            {product.sku || product.marca || 'S/N'}
                                        </span>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <span className="badge-custom badge-secondary">{product.tipo}</span>
                                            <span className={`badge-custom ${product.activo ? 'badge-success' : 'badge-danger'}`}>
                                                {product.activo ? 'Activo' : 'Inact.'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mobile-card-body">
                                        <div className="mobile-card-title">{product.nombre}</div>
                                        {product.descripcion && <div className="mobile-card-subtitle">{product.descripcion.substring(0, 50)}...</div>}

                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '13px' }}>
                                            <div style={{ color: 'var(--gray-500)' }}>
                                                Neto: <span style={{ color: 'var(--text-dark)', fontWeight: '500' }}>{formatCurrency(product.precio)}</span>
                                            </div>
                                            <div style={{ color: 'var(--gray-500)' }}>
                                                Imp: <span style={{ color: 'var(--text-dark)', fontWeight: '500' }}>{product.impuesto_total}%</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mobile-card-footer">
                                        <div className="mobile-card-total">
                                            {formatCurrency(productsService.calculateTotal(product.precio, product.impuesto_total))}
                                        </div>
                                        <div className="mobile-card-actions">
                                            {user?.rol === 'ADMIN' ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => navigate(`/app/cotizaplus/productos/editar/${product.id}`)}
                                                        style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                                    >
                                                        <FiEdit2 size={14} /> Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDelete(product.id);
                                                        }}
                                                        style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                                    >
                                                        <FiTrash2 size={14} /> Eliminar
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/app/cotizaplus/productos/editar/${product.id}?mode=request`)}
                                                    className="btn-custom"
                                                    style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--gray-100)', color: 'var(--text-dark)', border: '1px solid var(--gray-300)' }}
                                                >
                                                    Solicitar Cambio
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>


            {/* Botón Flotante para Móviles */}
            {user?.rol === 'ADMIN' && (
                <button
                    className="fab-custom d-lg-none"
                    onClick={() => navigate('/app/cotizaplus/productos/nuevo')}
                    title="Nuevo Producto"
                >
                    <FiPlus size={28} />
                </button>
            )}
        </div>
    );
};

export default ProductList;
